import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { DevicePresenceService, presenceLabel } from '../../services/device-presence.service';

/** Online/offline dot from the heartbeats, with the state as text when asked;
 * nothing for a device that is not monitored. */
@Component({
  selector: 'urban-presence-dot',
  template: `
    @if (state()?.presence !== 'unknown' && label(); as l) {
      <span class="uvPresenceDot" [attr.data-presence]="state()?.presence" role="img"
            [attr.aria-label]="l.key | translate: { count: l.count }"
            [matTooltip]="l.key | translate: { count: l.count }"></span>
      @if (withLabel()) {
        <span class="paragraphText secondaryTextColor">{{ l.key | translate: { count: l.count } }}</span>
      }
    }
  `,
  styles: [':host { display: inline-flex; align-items: center; vertical-align: middle; } .uvPresenceDot { margin-right: 8px; }'],
  imports: [MatTooltipModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PresenceDotComponent {
  public readonly deviceId = input<string | null | undefined>(null);
  public readonly withLabel = input<boolean>(false);

  private readonly service = inject(DevicePresenceService);

  protected readonly state = toSignal(
    toObservable(this.deviceId).pipe(
      distinctUntilChanged(),
      switchMap(id => this.service.watch(id))
    )
  );

  protected readonly label = computed(() => {
    const state = this.state();
    return state ? presenceLabel(state) : null;
  });
}
